(() => {
  'use strict';
  if (window.__uniAdminInteractions) return;
  window.__uniAdminInteractions = true;
  const instances = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 699px)');
  function init(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const on = { signal: abort.signal };
    const animations = new Set();
    const allowed = () => !reduced.matches && document.body.dataset.motion !== 'none' && document.body.dataset.motionUser !== 'none' && root.dataset.motion !== 'none' && (!window.UniMotion?.allowed || window.UniMotion.allowed(root));
    const animate = (element, frames, options = {}) => {
      if (!element || !allowed() || !element.animate) return;
      const animation = element.animate(frames, { duration: 580, easing: 'cubic-bezier(.2,.7,.2,1)', ...options });
      animations.add(animation);
      animation.finished.then(() => animations.delete(animation), () => animations.delete(animation));
      return animation;
    };
    const syncMotion = () => {
      root.dataset.studyMotion = allowed() ? 'on' : 'off';
      if (!allowed()) animations.forEach(animation => { animation.cancel(); });
    };
    syncMotion();
    reduced.addEventListener('change', syncMotion, on);
    document.addEventListener('uni:motionchange', syncMotion, on);
    const preferenceObserver = new MutationObserver(syncMotion);
    preferenceObserver.observe(document.body, { attributes: true, attributeFilter: ['data-motion', 'data-motion-user'] });
    preferenceObserver.observe(root, { attributes: true, attributeFilter: ['data-motion'] });
    const illustrations = root.querySelectorAll('.uni-admin-step-art,.uni-admin-case-art');
    const enter = element => {
      if (element.dataset.studyRevealed) return;
      element.dataset.studyRevealed = 'true';
      const artwork = element.querySelector('.uni-admin-case-image,.uni-admin-paper,svg') || element;
      const resting = getComputedStyle(artwork).transform;
      animate(artwork, [{ opacity: .85, transform: 'translateY(10px) scale(.99)' }, { opacity: 1, transform: resting }], { duration: 720 });
      if (element.classList.contains('uni-admin-step-art')) {
        element.closest('li')?.classList.add('is-step-seen');
        const parts = [...artwork.children].slice(-2);
        parts.forEach((part, index) => animate(part, [{ opacity: .1, translate: '0 8px' }, { opacity: 1, translate: '0 0' }], { duration: 450, delay: 180 + index * 80 }));
      }
    };
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) { enter(entry.target); observer.unobserve(entry.target); } });
    }, { threshold: .3, rootMargin: '0px 0px -15px 0px' }) : null;
    illustrations.forEach(element => observer ? observer.observe(element) : enter(element));
    const paperMotion = new WeakMap();
    root.querySelectorAll('.uni-admin-case').forEach(card => {
      let touchStart = null;
      card.addEventListener('pointerdown', event => {
        touchStart = event.pointerType !== 'mouse' ? { x: event.clientX, y: event.clientY } : null;
      }, on);
      card.addEventListener('pointercancel', () => { touchStart = null; }, on);
      card.addEventListener('pointerup', event => {
        const start = touchStart;
        touchStart = null;
        if (!start || !allowed() || Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) return;
        const paper = card.querySelector('.uni-admin-case-image,.uni-admin-paper');
        if (!paper) return;
        paperMotion.get(paper)?.cancel();
        const resting = getComputedStyle(paper).transform;
        const lifted = paper.classList.contains('uni-admin-case-image') ? 'translateY(-2px) scale(1.025)' : 'translateY(-8px) rotate(0deg)';
        paperMotion.set(paper, animate(paper, [{ transform: resting }, { transform: lifted, offset: .4 }, { transform: resting }], { duration: 520 }));
      }, on);
    });
    const feedback = '.uni-admin-case,.uni-distance-steps>li,.uni-distance-topics summary,.uni-program-hero__actions .uni-button,.uni-distance-family .uni-button';
    let pressed = null;
    const release = () => { pressed?.classList.remove('is-study-pressed'); pressed = null; };
    root.addEventListener('pointerdown', event => {
      release();
      if (event.button > 0) return;
      pressed = event.target.closest(feedback);
      pressed?.classList.add('is-study-pressed');
    }, on);
    window.addEventListener('pointerup', release, on);
    window.addEventListener('pointercancel', release, on);
    root.addEventListener('pointermove', event => { if (event.pointerType !== 'mouse') release(); }, { ...on, passive: true });
    const explorer = root.querySelector('[data-role-explorer]');
    const track = explorer?.querySelector('.uni-role-track');
    const cards = track ? [...track.querySelectorAll('[data-role-card]')] : [];
    const controls = explorer?.querySelector('.uni-role-controls');
    const previous = explorer?.querySelector('[data-role-prev]');
    const next = explorer?.querySelector('[data-role-next]');
    const current = explorer?.querySelector('[data-role-current]');
    let selected = 0;
    let scrollFrame = 0;
    const indicator = index => {
      selected = index;
      if (current) current.textContent = String(index + 1).padStart(2, '0');
      if (previous) previous.disabled = index === 0;
      if (next) next.disabled = index === cards.length - 1;
      cards.forEach((card, i) => card.closest('li').classList.toggle('is-role-current', i === index));
    };
    const nearest = () => {
      const left = track.getBoundingClientRect().left;
      return cards.reduce((best, card, i) => Math.abs(card.closest('li').getBoundingClientRect().left - left) < Math.abs(cards[best].closest('li').getBoundingClientRect().left - left) ? i : best, 0);
    };
    const go = index => {
      index = Math.max(0, Math.min(cards.length - 1, index));
      indicator(index);
      if (mobile.matches) {
        const left = cards[index].closest('li').offsetLeft - cards[0].closest('li').offsetLeft;
        track.scrollTo({ left, behavior: allowed() ? 'smooth' : 'instant' });
      }
    };
    if (track && cards.length) {
      controls.hidden = false;
      indicator(0);
      previous.addEventListener('click', () => go(selected - 1), on);
      next.addEventListener('click', () => go(selected + 1), on);
      track.addEventListener('keydown', event => {
        if (!mobile.matches || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
        event.preventDefault();
        go(selected + (event.key === 'ArrowRight' ? 1 : -1));
      }, on);
      track.addEventListener('scroll', () => {
        if (!mobile.matches) return;
        if (!scrollFrame) scrollFrame = requestAnimationFrame(() => { scrollFrame = 0; indicator(nearest()); });
      }, { ...on, passive: true });
      mobile.addEventListener('change', () => { if (mobile.matches) go(selected); }, on);
    }
    instances.set(root, () => {
      abort.abort(); release(); observer?.disconnect(); preferenceObserver.disconnect();
      animations.forEach(animation => animation.cancel());
      cancelAnimationFrame(scrollFrame);
      delete root.dataset.studyMotion;
    });
  }
  const within = scope => {
    if (scope.matches?.('uni-program[data-visual-cards="true"]')) init(scope);
    scope.querySelectorAll?.('uni-program[data-visual-cards="true"]').forEach(init);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => within(document), { once: true });
  else within(document);
  document.addEventListener('shopify:section:load', event => within(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, dispose] of instances) if (event.target === root || event.target.contains(root)) { dispose(); instances.delete(root); }
  });
})();
